import type {CompRef, CompStructure, Pointer, PS} from '@wix/document-services-types'
import _ from 'lodash'
import component from '../../component/component'
import componentsMetaData from '../../componentsMetaData/componentsMetaData'
import appControllerData from '../../appControllerData/appControllerData'

function getWidgetRootMetaData(ps: PS, compRef: Pointer, metaDataFn: (ps: PS, widgetRootRef: Pointer) => any) {
    const [widgetRootRef] = component.getChildren(ps, compRef)
    return metaDataFn(ps, widgetRootRef)
}

function getFromStageData(ps: PS, stageDataKey: string, compRef: Pointer) {
    return _.get(appControllerData.getControllerStageDataByControllerRef(ps, compRef), ['behavior', stageDataKey])
}

function rotatable(ps: PS, metaDataValue, compRef: Pointer) {
    return (
        getWidgetRootMetaData(ps, compRef, componentsMetaData.public.isRotatable) &&
        getFromStageData(ps, 'rotatable', compRef) !== false
    )
}

function resizableSides(ps: PS, metaDataValue, compRef: Pointer) {
    return _.intersection(
        getWidgetRootMetaData(ps, compRef, componentsMetaData.public.getResizableSides),
        getFromStageData(ps, 'resizable', compRef) === false ? [] : metaDataValue
    )
}

const calcMinMax = (
    mathFn: (...args: number[]) => number,
    key: string,
    appWidgetControllerLayoutLimits,
    widgetRootLayoutLimits
) => {
    const definedValues = [appWidgetControllerLayoutLimits?.[key], widgetRootLayoutLimits?.[key]].filter(
        value => !_.isUndefined(value)
    )
    if (definedValues.length) {
        return mathFn(...definedValues)
    }
}

const getAppWidgetLayoutLimit = (appWidgetControllerLayoutLimits, widgetRootLayoutLimits) => {
    if (_.isEmpty(appWidgetControllerLayoutLimits)) {
        return widgetRootLayoutLimits
    }

    return _.omitBy(
        {
            aspectRatio: widgetRootLayoutLimits?.aspectRatio,
            maxWidth: calcMinMax(Math.min, 'maxWidth', widgetRootLayoutLimits, appWidgetControllerLayoutLimits),
            minWidth: calcMinMax(Math.max, 'minWidth', widgetRootLayoutLimits, appWidgetControllerLayoutLimits),
            maxHeight: calcMinMax(Math.min, 'maxHeight', widgetRootLayoutLimits, appWidgetControllerLayoutLimits),
            minHeight: calcMinMax(Math.max, 'minHeight', widgetRootLayoutLimits, appWidgetControllerLayoutLimits)
        },
        _.isUndefined
    )
}

function layoutLimits(ps: PS, metaDataValue, compRef: Pointer) {
    const widgetRootLayoutLimits = getWidgetRootMetaData(ps, compRef, componentsMetaData.public.getLayoutLimits)
    const appWidgetLayoutLimits = getAppWidgetLayoutLimit(metaDataValue, widgetRootLayoutLimits)
    const manifestPreventsResize = getFromStageData(ps, 'resizable', compRef) === false
    if (manifestPreventsResize) {
        delete appWidgetLayoutLimits.aspectRatio
    }
    return appWidgetLayoutLimits
}

/**
 * @param {PS} ps
 * @param metaDataValue
 * @param {CompRef} compRef
 * @param {CompRef} potentialContainerPointer
 * @returns {boolean}
 */
const containable = (ps: PS, metaDataValue, compRef: CompRef, potentialContainerPointer: CompRef): boolean => {
    if (metaDataValue === false) {
        return false
    }
    const parentRef = component.getContainer(ps, compRef)
    if (ps.pointers.components.isSameComponent(parentRef, potentialContainerPointer)) {
        return true
    }
    if (component.getType(ps, potentialContainerPointer) === 'platform.components.AppWidget') {
        const rootChild = _.head(component.getChildren(ps, potentialContainerPointer))
        return componentsMetaData.public.isContainable(ps, compRef, rootChild)
    }
    return component.getType(ps, parentRef) !== 'platform.components.AppWidget'
}

/**
 * @param {PS} ps
 * @param metaDataValue
 * @param {CompStructure} compStructure
 * @param {CompRef} potentialContainerPointer
 * @returns {boolean}
 */
const containableByStructure = (
    ps: PS,
    metaDataValue,
    compStructure: CompStructure,
    potentialContainerPointer: CompRef
): boolean => {
    if (metaDataValue === false) {
        return false
    }

    if (component.getType(ps, potentialContainerPointer) === 'platform.components.AppWidget') {
        const rootChild = _.head(component.getChildren(ps, potentialContainerPointer))
        if (!rootChild) {
            return metaDataValue
        }
        return componentsMetaData.public.isContainableByStructure(ps, compStructure, rootChild)
    }
    return true
}

export default {
    rotatable,
    resizableSides,
    layoutLimits,
    containable,
    containableByStructure
}
